/**
 * Timesheets — admin weekly view of driver + driver-helper punches.
 *
 *   • Week toggle (Fri→Thu pay week ending on the picked date).
 *   • Per-employee card with a table of punches + running total.
 *   • Row-level edit / delete via a shadcn dialog.
 *   • "Send report now" button mirrors the Thursday 5 PM cron.
 *   • "Manual add" button back-fills a missed punch.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, Pencil, Trash2, Mail, PlusCircle, ChevronLeft, ChevronRight } from "lucide-react";

const CT = "America/Chicago";

// Convert an ISO string → HTML datetime-local value in Central Time.
// The <input type="datetime-local"> element only speaks "local" without a
// timezone offset, so we manually format it as CT for the admin to edit.
function isoToCentralLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    // Intl gives us CT year/month/day/hour/minute — pack into datetime-local shape.
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: CT, year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

// Turn a datetime-local string (interpreted as CT) back into a UTC ISO.
function centralLocalInputToIso(local) {
    if (!local) return null;
    // Build the CT datetime, then compute equivalent UTC.
    // Trick: create a Date from a string with a fake TZ, then use offset math.
    // Easier: use the tz offset from Intl at the same instant.
    const [d, t] = local.split("T");
    const [Y, M, D] = d.split("-").map(Number);
    const [h, m] = t.split(":").map(Number);
    // Guess an initial UTC — assume CT is UTC-6 (standard) then correct via Intl.
    let utc = Date.UTC(Y, M - 1, D, h + 6, m);
    // Intl-derived offset for that instant, in minutes
    const off = getCTOffsetMinutes(new Date(utc));
    utc = Date.UTC(Y, M - 1, D, h, m) + off * 60_000;
    return new Date(utc).toISOString();
}

function getCTOffsetMinutes(date) {
    // Minutes to ADD to a naive CT time to get UTC. CST=+360, CDT=+300.
    const fmt = new Intl.DateTimeFormat("en-US", { timeZone: CT, timeZoneName: "shortOffset" })
        .format(date);
    // e.g. "7/30/2026, GMT-5" → -5. If no offset present, default to CST.
    const m = /GMT([+-]\d+)/.exec(fmt);
    if (!m) return 360;
    const hours = parseInt(m[1], 10);
    return -hours * 60;
}

function fmtCT(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
        timeZone: CT, weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    });
}

function weekStartSunday(anchor) {
    // Return the most recent Sunday (in CT) as an ISO string. Pay weeks
    // run Sunday → Saturday and the Thursday cron fires the *next* Sunday
    // morning after the period closes.
    const d = new Date(anchor);
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: CT, weekday: "short", year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const weekdayIdx = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(parts.weekday);
    const daysSinceSunday = (weekdayIdx - 0 + 7) % 7;
    const dt = new Date(`${parts.year}-${parts.month}-${parts.day}T12:00:00Z`);
    dt.setUTCDate(dt.getUTCDate() - daysSinceSunday);
    return dt.toISOString();
}

export default function Timesheets() {
    // week_start is an ISO date INSIDE the target pay week — the backend
    // rounds it back to the enclosing Friday.
    const [anchor, setAnchor] = useState(() => weekStartSunday(new Date()));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [drivers, setDrivers] = useState([]);
    const [editing, setEditing] = useState(null); // entry object
    const [deleting, setDeleting] = useState(null);
    const [manualOpen, setManualOpen] = useState(false);
    const [manual, setManual] = useState({ user_id: "", clock_in: "", clock_out: "", note: "" });
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/timeclock/entries", { params: { week_start: anchor } });
            setData(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load timesheets");
        } finally {
            setLoading(false);
        }
    }, [anchor]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get("/users").then(({ data }) => {
            setDrivers(data.filter((u) => u.role === "driver" || u.role === "driver_helper"));
        }).catch(() => { /* non-fatal */ });
    }, []);

    const weekLabel = useMemo(() => {
        if (!data) return "";
        const s = new Date(data.week_start_ct);
        const e = new Date(data.week_end_ct);
        const opts = { timeZone: CT, month: "short", day: "numeric" };
        return `${s.toLocaleDateString("en-US", opts)} → ${e.toLocaleDateString("en-US", { ...opts, year: "numeric" })}`;
    }, [data]);

    const stepWeek = (deltaDays) => {
        const d = new Date(anchor);
        d.setUTCDate(d.getUTCDate() + deltaDays);
        setAnchor(d.toISOString());
    };

    const saveEdit = async () => {
        try {
            await api.patch(`/timeclock/entries/${editing.id}`, {
                clock_in: centralLocalInputToIso(editing._clock_in_local),
                clock_out: editing._clock_out_local
                    ? centralLocalInputToIso(editing._clock_out_local) : "",
                note: editing.note || "",
            });
            toast.success("Punch updated");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        }
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/timeclock/entries/${deleting.id}`);
            toast.success("Punch deleted");
            setDeleting(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
        }
    };

    const saveManual = async () => {
        try {
            await api.post("/timeclock/entries/manual", {
                user_id: manual.user_id,
                clock_in: centralLocalInputToIso(manual.clock_in),
                clock_out: manual.clock_out ? centralLocalInputToIso(manual.clock_out) : null,
                note: manual.note,
            });
            toast.success("Manual punch added");
            setManualOpen(false);
            setManual({ user_id: "", clock_in: "", clock_out: "", note: "" });
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add failed");
        }
    };

    const sendNow = async () => {
        if (!window.confirm("Send the weekly hours email to accounting@, community@ and customerservice@ right now?"))
            return;
        setSending(true);
        try {
            const { data } = await api.post("/timeclock/report/send-now");
            toast.success(`Sent to ${data.recipients?.length || 0} recipients — ${data.grand_total_hours} total hrs`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="timesheets-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Payroll</div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Timesheets</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Driver + helper punches. Pay week runs Sunday → Saturday.
                        Auto-emails Sunday 8 AM Central (the morning after the
                        period closes) to accounting, community, and customer service.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setManualOpen(true)} data-testid="timesheets-add-punch">
                        <PlusCircle className="w-4 h-4 mr-2" />Add punch
                    </Button>
                    <Button onClick={sendNow} disabled={sending} data-testid="timesheets-send-now">
                        <Mail className="w-4 h-4 mr-2" />{sending ? "Sending…" : "Send report now"}
                    </Button>
                </div>
            </div>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span data-testid="timesheets-week-label">Pay week — {weekLabel}</span>
                    </CardTitle>
                    <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => stepWeek(-7)}
                                data-testid="timesheets-prev-week">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setAnchor(weekStartSunday(new Date()))}
                                data-testid="timesheets-this-week">This week</Button>
                        <Button size="sm" variant="ghost" onClick={() => stepWeek(7)}
                                data-testid="timesheets-next-week">
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading && <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>}
                    {!loading && data?.by_user?.length === 0 && (
                        <div className="text-sm text-muted-foreground py-10 text-center"
                             data-testid="timesheets-empty">
                            No punches recorded this pay week.
                        </div>
                    )}
                    {!loading && data?.by_user?.length > 0 && (
                        <div className="space-y-5">
                            {data.by_user.map((u) => (
                                <UserBlock key={u.user_id} u={u}
                                    onEdit={(e) => setEditing({
                                        ...e,
                                        _clock_in_local: isoToCentralLocalInput(e.clock_in),
                                        _clock_out_local: e.clock_out ? isoToCentralLocalInput(e.clock_out) : "",
                                    })}
                                    onDelete={setDeleting} />
                            ))}
                            <div className="pt-4 border-t border-border flex justify-end">
                                <div className="text-sm">
                                    <span className="text-muted-foreground">Grand total: </span>
                                    <span className="font-heading text-2xl font-bold ml-2"
                                          data-testid="timesheets-grand-total">
                                        {data.grand_total_hours} hrs
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent data-testid="timesheets-edit-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit punch — {editing?.user_name}</DialogTitle>
                        <DialogDescription className="text-xs">
                            Times are Central. Leave "Clock out" blank to reopen the punch.
                        </DialogDescription>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <Field label="Clock in">
                                <Input type="datetime-local"
                                       value={editing._clock_in_local}
                                       onChange={(e) => setEditing({ ...editing, _clock_in_local: e.target.value })}
                                       data-testid="edit-clock-in" />
                            </Field>
                            <Field label="Clock out">
                                <Input type="datetime-local"
                                       value={editing._clock_out_local || ""}
                                       onChange={(e) => setEditing({ ...editing, _clock_out_local: e.target.value })}
                                       data-testid="edit-clock-out" />
                            </Field>
                            <Field label="Note">
                                <Textarea value={editing.note || ""}
                                          onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                                          rows={2}
                                          data-testid="edit-note" />
                            </Field>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button onClick={saveEdit} data-testid="edit-save">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <DialogContent data-testid="timesheets-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete this punch?</DialogTitle>
                        <DialogDescription className="text-xs">
                            {deleting?.user_name} · {fmtCT(deleting?.clock_in)} →{" "}
                            {deleting?.clock_out ? fmtCT(deleting.clock_out) : "(open)"}.
                            This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={confirmDelete} data-testid="confirm-delete">
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Manual add */}
            <Dialog open={manualOpen} onOpenChange={setManualOpen}>
                <DialogContent data-testid="timesheets-manual-dialog">
                    <DialogHeader>
                        <DialogTitle>Add a punch manually</DialogTitle>
                        <DialogDescription className="text-xs">
                            Use this for missed clock-ins / -outs. Times are Central.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Field label="Employee">
                            <Select value={manual.user_id}
                                    onValueChange={(v) => setManual({ ...manual, user_id: v })}>
                                <SelectTrigger data-testid="manual-employee"><SelectValue placeholder="Pick a driver" /></SelectTrigger>
                                <SelectContent>
                                    {drivers.map((d) => (
                                        <SelectItem key={d.id} value={d.id}>
                                            {d.name} · {d.role === "driver_helper" ? "Helper" : "Driver"}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Clock in">
                            <Input type="datetime-local"
                                   value={manual.clock_in}
                                   onChange={(e) => setManual({ ...manual, clock_in: e.target.value })}
                                   data-testid="manual-clock-in" />
                        </Field>
                        <Field label="Clock out (optional)">
                            <Input type="datetime-local"
                                   value={manual.clock_out}
                                   onChange={(e) => setManual({ ...manual, clock_out: e.target.value })}
                                   data-testid="manual-clock-out" />
                        </Field>
                        <Field label="Note (optional)">
                            <Textarea value={manual.note}
                                      onChange={(e) => setManual({ ...manual, note: e.target.value })}
                                      rows={2}
                                      data-testid="manual-note" />
                        </Field>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setManualOpen(false)}>Cancel</Button>
                        <Button onClick={saveManual}
                                disabled={!manual.user_id || !manual.clock_in}
                                data-testid="manual-save">Save punch</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function UserBlock({ u, onEdit, onDelete }) {
    return (
        <div className="border border-border rounded-md overflow-hidden"
             data-testid={`timesheets-user-${u.user_id}`}>
            <div className="flex items-center justify-between px-4 py-2 bg-secondary/40">
                <div className="flex items-center gap-3">
                    <div className="font-semibold">{u.user_name}</div>
                    <Badge variant="outline" className="text-[10px] tracking-wider">
                        {u.role === "driver_helper" ? "HELPER" : "DRIVER"}
                    </Badge>
                    {u.has_open && (
                        <Badge className="bg-red-500/10 text-red-600 border-red-500/30 text-[10px]">
                            OPEN PUNCH
                        </Badge>
                    )}
                </div>
                <div className="text-sm font-bold" data-testid={`timesheets-user-total-${u.user_id}`}>
                    {u.total_hours} hrs
                </div>
            </div>
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="text-left px-4 py-2">Clock in</th>
                        <th className="text-left px-4 py-2">Clock out</th>
                        <th className="text-right px-4 py-2">Hours</th>
                        <th className="text-right px-4 py-2 w-24"></th>
                    </tr>
                </thead>
                <tbody>
                    {u.entries.map((e) => (
                        <tr key={e.id} className="border-t border-border">
                            <td className="px-4 py-2">{fmtCT(e.clock_in)}</td>
                            <td className="px-4 py-2">
                                {e.clock_out ? fmtCT(e.clock_out)
                                    : <span className="text-red-600 font-medium">still clocked in</span>}
                            </td>
                            <td className="px-4 py-2 text-right font-mono">{e.hours ?? 0}</td>
                            <td className="px-4 py-2 text-right space-x-1">
                                <Button size="icon" variant="ghost" onClick={() => onEdit(e)}
                                        data-testid={`edit-punch-${e.id}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => onDelete(e)}
                                        data-testid={`delete-punch-${e.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs">{label}</Label>
            {children}
        </div>
    );
}
